import * as Sentry from '@sentry/vue';
import { App } from 'vue';
import { Router } from 'vue-router';

function normalizeUnhandledRejectionReason(reason: unknown) {
   if (reason instanceof Error) return reason;

   if (typeof reason === 'string') {
      return new Error(reason);
   }

   if (reason === undefined || reason === null) {
      return new Error('UnhandledRejection: reason was undefined');
   }

   return new Error('UnhandledRejection: non-Error reason');
}

function initSentry(app: App, router: Router) {
   const browserTracing = Sentry.browserTracingIntegration({ router });
   const enabled = ['acc', 'prod', 'release'].includes(import.meta.env.VITE_APP_ENV);

   window.addEventListener('unhandledrejection', (event) => {
      if (event.reason !== undefined && event.reason !== null) return;

      event.preventDefault();

      Sentry.captureException(normalizeUnhandledRejectionReason(event.reason), {
         tags: {
            mechanism: 'onunhandledrejection',
         },
         extra: {
            routeName: router.currentRoute.value.name,
            routePath: router.currentRoute.value.path,
            routeQuery: router.currentRoute.value.query,
            reason: event.reason,
         },
      });
   });

   Sentry.init({
      app,
      attachProps: true,
      attachStacktrace: true,
      dsn: import.meta.env.VITE_APP_SENTRY_DSN,
      enabled,
      environment: import.meta.env.VITE_APP_ENV,
      integrations: [browserTracing],
      logErrors: true,
      maxBreadcrumbs: 50,
      release: import.meta.env.VITE_APP_VERSION,
      tracesSampleRate: 0.1,
      trackComponents: true,
      ignoreErrors: [
         'Request aborted',
         'AxiosError',
         'reCAPTCHA Timeout',
         'UnhandledRejection: reason was undefined',
         "NotAllowedError: Failed to execute 'readText' on 'Clipboard': Read permission denied.",
         'Unable to preload CSS',
         // Ignore dynamic import failures - these happen when users have cached old HTML after new deployments
         /Failed to fetch dynamically imported module/i,
         /error loading dynamically imported module/i,
         /dynamically imported module/i,
      ],
      beforeSend(event) {
         // Additional filter for dynamic import and service worker errors
         if (event.exception?.values?.[0]?.value) {
            const errorMessage = event.exception.values[0].value.toLowerCase();
            const stackFrames = event.exception.values[0].stacktrace?.frames ?? [];
            const hasServiceWorkerFrame = stackFrames.some((frame) => {
               const frameFilename = frame.filename?.toLowerCase() ?? '';
               const frameFunction = frame.function?.toLowerCase() ?? '';

               return frameFilename.includes('sw.js') || frameFunction.includes('serviceworker.register');
            });

            // Ignore dynamic import failures (deployment/caching issues, not code bugs)
            if (
               errorMessage.includes('dynamically imported module') ||
               errorMessage.includes('failed to fetch') ||
               errorMessage.includes('dynamic import')
            ) {
               return null;
            }

            // Ignore service worker script load failures (infrastructure issues)
            if (errorMessage.includes('script') && errorMessage.includes('sw.js') && errorMessage.includes('load failed')) {
               return null;
            }

            // Ignore browser/runtime noise that users can't meaningfully act on.
            if (
               errorMessage.includes('recaptcha timeout') ||
               errorMessage.includes('unable to preload css') ||
               errorMessage.includes('read permission denied') ||
               errorMessage.includes('service worker') ||
               errorMessage.includes('unhandledrejection: reason was undefined')
            ) {
               return null;
            }

            if (errorMessage === 'error: rejected' && hasServiceWorkerFrame) {
               return null;
            }
         }

         return event;
      },
   });
}

export { initSentry };
